import { PromotionType, useAnalyticsTracking } from "@configs/analytics";
import { useEffect, useRef } from "react";

export interface UsePromotionEvaluatedOptions {
  promotionType: PromotionType;
  promotionId?: string;
  eligibilityStatus: "eligible" | "ineligible";
  /** Omit when the mechanic has no concept of hidden/disabled separate from eligibility. */
  visibilityStatus?: "shown" | "hidden" | "disabled";
  /** Hold fire when the status hasn't been computed from a real check yet (e.g. still fetching, or a guest-mode placeholder). Defaults to true. */
  enabled?: boolean;
}

/**
 * Frontend approximation of the spec's `evt:promotion_evaluated`. Fires once per
 * mechanic per tab load, then again only when the eligibility/visibility outcome
 * changes — e.g. a cooldown ending flips `ineligible` to `eligible`.
 *
 * Dedup is a module-level map, not a `useRef`: several components can mount the
 * same mechanic's status hook simultaneously (e.g. a FAB and a dedicated page both
 * calling `useCSCompensation()`), and they must share one fired-state rather than
 * each firing independently.
 *
 * Known limitation: this cannot see promotions gated out before they ever reach a
 * client API response — only backend can close that gap.
 */
const lastFiredKeyByType = new Map<PromotionType, string>();

/** Call on logout so the next member's real evaluations aren't deduped against the previous member's. */
export function resetPromotionEvaluatedDedup(): void {
  lastFiredKeyByType.clear();
}

export function usePromotionEvaluated(options: UsePromotionEvaluatedOptions): void {
  const { trackPromotionEvaluated } = useAnalyticsTracking();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const { promotionType, promotionId, eligibilityStatus, visibilityStatus, enabled = true } = optionsRef.current;
    if (!enabled) return;
    const key = `${eligibilityStatus}|${visibilityStatus ?? ""}`;
    if (lastFiredKeyByType.get(promotionType) === key) return;
    lastFiredKeyByType.set(promotionType, key);
    trackPromotionEvaluated({
      promotionType,
      promotionId,
      surfacePage: window.location.pathname,
      eligibilityStatus,
      visibilityStatus,
    });
  }, [
    options.promotionType,
    options.promotionId,
    options.eligibilityStatus,
    options.visibilityStatus,
    options.enabled,
    trackPromotionEvaluated,
  ]);
}
